import ko from 'knockout';
import { customFetch as fetch, CustomError } from '../fetch';
import { AttachmentType } from './databaseModels';

export interface CompanyDocumentModel {
    id: string;
    name: string;
    type: AttachmentType;
}

export class CompanyDocumentRow {
    baseUrl: string;
    companyId: () => string;
    type: AttachmentType;
    label: string;
    single: boolean;

    document: KnockoutObservable<CompanyDocumentModel | null>;
    items: KnockoutObservableArray<CompanyDocumentModel>;
    documents: KnockoutComputed<CompanyDocumentModel[]>;
    statusText: KnockoutComputed<string>;
    showUpload: KnockoutComputed<boolean>;

    uploading: KnockoutObservable<boolean>;
    deletingIds: KnockoutObservableArray<string>;
    errors: KnockoutObservableArray<CustomError>;
    busy: KnockoutComputed<boolean>;

    constructor(
        baseUrl: string,
        companyId: () => string,
        type: AttachmentType,
        label: string,
        single: boolean = true,
    ) {
        this.baseUrl = baseUrl;
        this.companyId = companyId;
        this.type = type;
        this.label = label;
        this.single = single;

        this.document = ko.observable<CompanyDocumentModel | null>(null);
        this.items = ko.observableArray<CompanyDocumentModel>([]);
        this.uploading = ko.observable<boolean>(false);
        this.deletingIds = ko.observableArray<string>([]);
        this.errors = ko.observableArray<CustomError>();

        this.documents = ko.pureComputed(() =>
            this.single
                ? this.document()
                    ? [this.document() as CompanyDocumentModel]
                    : []
                : this.items(),
        );

        this.statusText = ko.pureComputed(() => {
            const count = this.documents().length;
            if (this.single) {
                return count > 0 ? 'Geüpload' : 'Niet geüpload';
            }
            return count > 0 ? `${count} bestand(en)` : 'Geen extra bestanden';
        });

        this.showUpload = ko.pureComputed(
            () => !this.single || this.documents().length === 0,
        );

        this.busy = ko.pureComputed(() => false);
    }

    setDocuments(documents: CompanyDocumentModel[]) {
        if (this.single) {
            this.document(documents[0] ?? null);
        } else {
            this.items(documents);
        }
    }

    isDeleting(id: string) {
        return this.deletingIds.indexOf(id) !== -1;
    }

    async onFileChange(_row: CompanyDocumentRow, event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';

        if (file) {
            await this.upload(file);
        }
    }

    private async upload(file: File) {
        this.errors([]);
        this.uploading(true);

        const fd = new FormData();
        fd.append('companyId', this.companyId());
        fd.append('type', this.type.toString());
        fd.append('file', file);

        const response = await fetch(`${this.baseUrl}/Add`, {
            method: 'POST',
            body: fd,
        });

        if (response.ok && response.data) {
            if (this.single) {
                this.document(response.data);
            } else {
                this.items.push(response.data);
            }
        } else if (response.errors) {
            this.errors(response.errors);
        }

        this.uploading(false);
    }

    async remove(document: CompanyDocumentModel) {
        this.errors([]);
        this.deletingIds.push(document.id);

        const response = await fetch(`${this.baseUrl}/Delete/${document.id}`, {
            method: 'POST',
        });

        if (response.ok) {
            if (this.single) {
                this.document(null);
            } else {
                this.items.remove((d) => d.id === document.id);
            }
        } else if (response.errors) {
            this.errors(response.errors);
        }

        this.deletingIds.remove(document.id);
    }

    download(document: CompanyDocumentModel) {
        window.open(`${this.baseUrl}/Download/${document.id}`, '_blank');
    }
}

export class CompanyDocuments {
    rows: CompanyDocumentRow[];
    busy: KnockoutComputed<boolean>;
    loading: KnockoutObservable<boolean>;

    constructor(area: 'Admin' | 'Employer', companyId: () => string) {
        const baseUrl = `/${area}/CompanyDocument`;
        this.loading = ko.observable<boolean>(true);
        this.rows = [
            new CompanyDocumentRow(
                baseUrl,
                companyId,
                AttachmentType.Map,
                'Plattegrond',
            ),
            new CompanyDocumentRow(
                baseUrl,
                companyId,
                AttachmentType.Briefing,
                'Briefing',
            ),
            new CompanyDocumentRow(
                baseUrl,
                companyId,
                AttachmentType.Other,
                'Extra documenten',
                false,
            ),
        ];

        this.busy = ko.pureComputed(() =>
            this.rows.some(
                (row) => row.uploading() || row.deletingIds().length > 0,
            ),
        );

        this.rows.forEach((row) => (row.busy = this.busy));

        this.load(baseUrl, companyId);
    }

    private async load(baseUrl: string, companyId: () => string) {
        const response = await fetch(`${baseUrl}/All/${companyId()}`);

        if (response.ok && response.data) {
            const documents: CompanyDocumentModel[] = response.data;
            this.rows.forEach((row) =>
                row.setDocuments(documents.filter((d) => d.type === row.type)),
            );
        }

        this.loading(false);
    }
}
